/**
 * useTripPlan — hook that orchestrates the trip computation pipeline.
 *
 * Inputs come from PlanPage URL params.
 * Outputs: route, soc trace, charging stops, costs, accuracy band.
 *
 * Pipeline:
 *   1. Geocode origin and destination
 *   2. Call Valhalla for route segments with elevation
 *   3. Fetch weather along route segments
 *   4. Run rangeEngine.planSoCAlongRoute
 *   5. If reserve breached, run chargingPlanner.planChargingStops
 *   6. Compute cost and locale-formatted totals
 */
import { useEffect, useState } from 'react';
import { getVehicleById } from '../lib/supabase';
import { planRoute, geocode } from '../lib/valhalla';
import { getWeatherAlongRoute } from '../lib/weather';
import { planSoCAlongRoute } from '../lib/rangeEngine';
import { planChargingStops } from '../lib/chargingPlanner';

export function useTripPlan(searchParams) {
  const [state, setState] = useState({
    loading: true,
    error: null,
    vehicle: null,
    route: null,
    socTrace: null,
    chargingStops: null,
    summary: null,
  });
  
  useEffect(() => {
    if (!searchParams) return;
    
    let cancelled = false;
    
    async function compute() {
      try {
        setState(s => ({ ...s, loading: true, error: null }));
        
        const vehicle_id = searchParams.get('vehicle_id');
        const origin_str = searchParams.get('origin');
        const destination_str = searchParams.get('destination');
        const start_soc = parseInt(searchParams.get('start_soc') || '90');
        const arrive_soc = parseInt(searchParams.get('arrive_soc') || '20');
        const max_soc = parseInt(searchParams.get('max_soc') || '80');
        const climate = searchParams.get('climate') || 'on';
        const driving_style = searchParams.get('style') || 'normal';
        const road_quality = searchParams.get('road_quality') || 'good';
        const tire_pressure_actual_kpa = parseInt(searchParams.get('tire_pressure_kpa') || '240');
        const drivetrain_mode = searchParams.get('drivetrain_mode') || 'auto';

        // ----------------------------------------------------------------------
        // v2 tire pressure context — all optional. If the URL provides
        // tire_check_date, we compute days_since and feed it to the engine,
        // which then applies the permeation-loss term. inflation_gas defaults
        // to 'air' on the engine side.
        // ----------------------------------------------------------------------
        const tire_check_date_str = searchParams.get('tire_check_date');  // YYYY-MM-DD
        const days_since_tire_check = tire_check_date_str
          ? Math.max(0, Math.floor((Date.now() - new Date(tire_check_date_str).getTime()) / 86400000))
          : undefined;
        const inflation_gas = searchParams.get('inflation_gas') || undefined;  // 'air' | 'n2'
        const departure_str = searchParams.get('departure');                   // ISO datetime
        const departureTime = departure_str ? new Date(departure_str) : new Date();
        
        if (!vehicle_id) throw new Error('No vehicle specified');
        if (!origin_str) throw new Error('No origin specified');
        if (!destination_str) throw new Error('No destination specified');
        
        const vehicle = await getVehicleById(vehicle_id);
        if (cancelled) return;
        if (!vehicle) throw new Error(`Vehicle ${vehicle_id} not found`);

        // Prefer the structured location from the autocomplete (origin_lat/lng/cc).
        // Falls back to text-only geocoding for legacy URLs and shared links
        // created before the autocomplete was added.
        const origin_lat = parseFloat(searchParams.get('origin_lat') || '');
        const origin_lng = parseFloat(searchParams.get('origin_lng') || '');
        const origin_cc = searchParams.get('origin_cc') || null;
        const dest_lat = parseFloat(searchParams.get('dest_lat') || '');
        const dest_lng = parseFloat(searchParams.get('dest_lng') || '');
        const dest_cc = searchParams.get('dest_cc') || null;

        const hasStructured = Number.isFinite(origin_lat) && Number.isFinite(origin_lng)
                           && Number.isFinite(dest_lat) && Number.isFinite(dest_lng);

        let origin, destination;
        if (hasStructured) {
          origin = { lat: origin_lat, lng: origin_lng, name: origin_str, country_code: origin_cc };
          destination = { lat: dest_lat, lng: dest_lng, name: destination_str, country_code: dest_cc };
        } else {
          // Legacy / shared-link path: geocode the text. This is best-effort
          // and may silently pick the wrong city for ambiguous names.
          [origin, destination] = await Promise.all([
            geocode(origin_str),
            geocode(destination_str),
          ]);
          if (cancelled) return;
          if (!origin) throw new Error(`Could not find location: ${origin_str}`);
          if (!destination) throw new Error(`Could not find location: ${destination_str}`);
        }
        
        const route = await planRoute(origin, destination, { elevation: true });
        if (cancelled) return;
        
        // Weather: sample 4 evenly-spaced waypoints along the route, fetch
        // hourly forecast from Open-Meteo at the chosen departure time, and
        // produce one weather entry per segment via linear interpolation.
        // Falls back to a neutral 20°C if Open-Meteo is unreachable.
        const weather = await getWeatherAlongRoute(route, {
          sample_count: 4,
          departureTime
        }).catch(() => 
          route.segments.map(() => ({ ambient_c: 20, wind_speed_kmh: 0, wind_direction_deg: 0 }))
        );
        if (cancelled) return;
        
        // Country code for drive-cycle locale adjustment. Prefer the origin's
        // country (we don't currently handle cross-border routes well — that's
        // future scope). Falls back to undefined which the engine treats as
        // "no locale adjustment" (1.0x multiplier).
        const country_code = origin?.country_code || destination?.country_code;

        const settings = {
          starting_soc_pct: start_soc,
          target_arrival_soc_pct: arrive_soc,
          max_soc_pct: max_soc,
          hvac_setting: climate,
          driving_style,
          road_quality,
          tire_pressure_actual_kpa,
          drivetrain_mode,
          // v2 tire pressure context — engine treats these as optional.
          // When days_since_tire_check is present and weather provides
          // ambient_c, the engine uses the temperature/permeation/warming
          // model from tirePressureV2.js.
          days_since_tire_check,
          inflation_gas,
          // Drive-cycle context — country_code lets the engine apply the
          // Heliyon-derived locale adjustment (e.g. +8% for India to capture
          // road-quality stop-go losses beyond what avg speed alone shows).
          country_code,
        };
        
        const socTrace = planSoCAlongRoute(route, vehicle, weather, settings);
        
        // Charging stops are deferred until Task F (station sync) is complete.
        // The chargingPlanner needs candidateStations from the database, which
        // we don't have populated yet. For now, if a trip would require charging,
        // we surface that to the UI as a "needs charging" flag rather than
        // crashing. Once stations are loaded, restore the planChargingStops()
        // call here.
        let chargingStops = null;
        const needsCharging = socTrace.final_soc_pct < arrive_soc;
        if (needsCharging) {
          chargingStops = {
            stops: [],
            needs_charging: true,
            warning: 'This trip needs charging stops, but the charging station database is not yet populated. Estimated arrival SoC shown is without stops.'
          };
        }
        
        const totalEnergyKwh = socTrace.total_energy_wh / 1000;
        const totalChargeMin = chargingStops?.stops?.reduce((s, st) => s + (st.charge_time_min || 0), 0) || 0;
        const totalCostLocal = chargingStops?.stops?.reduce((s, st) => s + (st.cost_local || 0), 0) || 0;
        
        if (cancelled) return;
        setState({
          loading: false,
          error: null,
          vehicle,
          route,
          socTrace,
          chargingStops,
          summary: {
            distance_km: route.total_distance_km,
            drive_time_min: route.total_drive_time_min,
            charge_time_min: totalChargeMin,
            total_time_min: route.total_drive_time_min + totalChargeMin,
            total_energy_kwh: totalEnergyKwh,
            final_soc_pct: socTrace.final_soc_pct,
            confidence_pts: socTrace.confidence_pts || 3,
            total_cost_local: totalCostLocal,
            origin, destination,
          },
        });
      } catch (e) {
        if (cancelled) return;
        setState(s => ({ ...s, loading: false, error: e.message }));
      }
    }
    
    compute();
    return () => { cancelled = true; };
  }, [searchParams]);
  
  return state;
}
